import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLang } from "../i18n/LanguageContext";
import { Eyebrow, Reveal, Rich } from "./Motion";

export const About = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);
  return (
    <section id="about" ref={ref} data-testid="about-section" className="relative bg-[var(--bone)] text-[var(--ink)] py-24 lg:py-40">
      <div className="max-w-[1440px] mx-auto px-5 lg:px-10 grid lg:grid-cols-12 gap-12 lg:gap-10">
        <div className="lg:col-span-4">
          <Eyebrow className="!text-[#b08d1f] [&>span]:!bg-[#b08d1f]">{t.about.eyebrow}</Eyebrow>
          <Reveal className="mt-12">
            <div data-testid="about-door" className="relative w-full [perspective:1400px]">
              <motion.div
                whileHover={{ rotateY: -10 }}
                transition={{ type: "spring", stiffness: 60, damping: 14 }}
                style={{ transformOrigin: "left center", transformStyle: "preserve-3d" }}
                className="relative h-[380px] lg:h-[440px] overflow-hidden rounded-t-[110px] rounded-b-[6px] shadow-[0_40px_90px_-40px_rgba(16,16,20,0.45)] border border-[rgba(16,16,20,0.12)]">
                <img src="/img/mariella-door.jpg" alt="Mariella Finizola apre la porta in vetro di MF Logistics Consulting Group" className="absolute inset-0 w-full h-full object-cover" />
              </motion.div>
              <p className="mono text-[10px] uppercase text-[rgba(16,16,20,0.6)] text-center mt-4">{t.about.doorCaption}</p>
            </div>
          </Reveal>
          <Reveal delay={0.2}>
            <div data-testid="about-founder" className="mt-10 flex items-center gap-5">
              <img src="/img/mariella-avatar.jpg" alt="Mariella Finizola" className="w-16 h-16 rounded-full object-cover shrink-0" />
              <div>
                <p className="serif text-2xl lg:text-3xl text-[var(--ink)]">Mariella Finizola</p>
                <p className="mono text-[11px] uppercase text-[#b08d1f] mt-1">{t.about.founderRole}</p>
              </div>
            </div>
            <p className="serif text-xl lg:text-2xl text-[rgba(16,16,20,0.85)] mt-4 max-w-sm">“{t.about.founderLine}”</p>
          </Reveal>
        </div>
        <div className="lg:col-span-8 lg:pl-10">
          <Reveal>
            <h2 data-testid="about-title" className="display text-4xl sm:text-5xl lg:text-7xl leading-[1.06] tracking-[-0.01em]"><Rich text={t.about.title} /></h2>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-8 mt-12 lg:mt-16 text-base lg:text-lg leading-relaxed text-[rgba(16,16,20,0.8)]">
            <Reveal delay={0.1}><p>{t.about.body1}</p></Reveal>
            <Reveal delay={0.2}><p>{t.about.body2}</p></Reveal>
          </div>
          <ul className="mt-16 lg:mt-24 border-t border-[rgba(16,16,20,0.15)]">
            {t.about.pillars.map((p, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <li data-testid={`about-pillar-${i}`} className="group grid grid-cols-12 gap-4 py-7 border-b border-[rgba(16,16,20,0.15)]">
                  <span className="col-span-2 mono text-[12px] text-[#b08d1f] pt-1.5">0{i + 1}</span>
                  <h3 className="col-span-10 md:col-span-4 text-xl lg:text-2xl font-semibold tracking-tight transition-transform duration-500 group-hover:translate-x-2">{p.t}</h3>
                  <p className="col-span-10 col-start-3 md:col-span-6 text-[rgba(16,16,20,0.75)]">{p.d}</p>
                </li>
              </Reveal>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
};
